import type {RefObject} from 'react';
import {useEffect, useRef} from 'react';
import {useAppDispatch} from '@/hooks';
import {updatePanningStatus, updateTimelineWidthPerSecond, TIMELINE_MAX_WIDTH_PER_SECOND} from '../timeline-reducer';
import {useTimelineWidthPerSecond} from './timeline-hooks';

/**
 * Custom hook that handles Ctrl+Wheel zoom functionality for the timeline.
 * Zooms in/out based on scroll direction while holding Ctrl/Cmd key.
 */
export function useTimelineWheelZoom(containerRef: RefObject<HTMLDivElement | null>): void {
  const dispatch = useAppDispatch();
  const widthPerSecond = useTimelineWidthPerSecond();
  const widthPerSecondRef = useRef(widthPerSecond);

  // Keep ref in sync for use in event handler
  useEffect(() => {
    widthPerSecondRef.current = widthPerSecond;
  }, [widthPerSecond]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let zoomTimer: number | undefined;

    const handleWheel = (e: WheelEvent): void => {
      if (!e.ctrlKey && !e.metaKey) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      dispatch(updatePanningStatus(true));

      const currentWidth = widthPerSecondRef.current;
      const scale = currentWidth > 80 ? 1 : (2 * currentWidth) / TIMELINE_MAX_WIDTH_PER_SECOND;
      dispatch(updateTimelineWidthPerSecond(currentWidth + e.deltaY * -1 * scale));

      if (zoomTimer) {
        clearTimeout(zoomTimer);
      }

      zoomTimer = window.setTimeout(() => {
        dispatch(updatePanningStatus(false));
      }, 500);
    };

    container.addEventListener('wheel', handleWheel, {passive: false});

    return () => {
      container.removeEventListener('wheel', handleWheel);
      if (zoomTimer) {
        clearTimeout(zoomTimer);
      }
    };
  }, [containerRef, dispatch]);
}
